import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { eventEndpoints, opponentEndpoints } from '../../api';
import { AccessibilityEditor } from '../../components/AccessibilityEditor';
import { Button, ErrorNote, Field, Panel } from '../../ui';
import { LOYALTY_RULE, MINIMUM_AGE_CHOICES, ageLabel } from '@bre-ticketing/domain';
import type { PanelProps } from '../Event';
import { INPUT } from './shared';
import { PromotionsPicker } from './PromotionsPicker';
import { useFeatures } from '../../features';
import { dateWords } from '../../terms';

const VISIBILITIES = ['PUBLIC', 'UNLISTED', 'PRIVATE'];

export function OverviewPanel({ detail, refresh, onError, can }: PanelProps) {
  // Loyalty's two questions are asked only where loyalty exists (D-369); the stored answers are kept either way.
  const features = useFeatures();
  const { event } = detail;
  const [title, setTitle] = useState(event.title);
  const [subtitle, setSubtitle] = useState(event.subtitle ?? '');
  const [visibility, setVisibility] = useState(event.visibility);
  const [featured, setFeatured] = useState(event.featured === 1);
  const [age, setAge] = useState(event.age_restriction ?? '');
  const [minimumAge, setMinimumAge] = useState(event.minimum_age ?? 0);
  const [perOrder, setPerOrder] = useState(event.ticket_limit_per_order?.toString() ?? '');
  const [perCustomer, setPerCustomer] = useState(event.ticket_limit_per_customer?.toString() ?? '');
  // What the reminder and the follow-up say (D-94, D-95). Written once per
  // event, read by every performance's messages.
  const [arrival, setArrival] = useState(event.arrival_note ?? '');
  const [parking, setParking] = useState(event.parking_note ?? '');
  const [postEvent, setPostEvent] = useState(event.post_event_note ?? '');
  const [survey, setSurvey] = useState(event.survey_url ?? '');
  // Loyalty is per event (D-121), and the two are separate questions: a
  // fundraiser can earn and not be discountable, a rental neither.
  const [loyaltyEarn, setLoyaltyEarn] = useState(event.loyalty_earn !== 0);
  const [loyaltyRedeem, setLoyaltyRedeem] = useState(event.loyalty_redeem !== 0);
  // A game is an event with an opponent (D-368); its promotions come from the library (D-377).
  const [opponentId, setOpponentId] = useState(event.opponent_id ?? '');
  // Per game (D-373): the owner's answers are checkboxes on each game, not rules on a batch.
  const [voucherExcluded, setVoucherExcluded] = useState(event.voucher_excluded === 1);
  const [playoffGame, setPlayoffGame] = useState(event.playoff_game === 1);
  // A playoff game played only if the series needs it (D-374).
  const [ifNecessary, setIfNecessary] = useState(event.if_necessary === 1);

  const editable = event.status !== 'ARCHIVED' && can('events.manage');

  // What this show offers on top of the room, and its "Good to know" lines (D-80).
  const queryClient = useQueryClient();
  // Every team, so a game already against an archived one still shows it; the
  // picker offers only the active ones besides that.
  const opponents = useQuery({ queryKey: ['opponents', 'all'], queryFn: () => opponentEndpoints.list(true) });
  const opponentChoices = (opponents.data?.opponents ?? []).filter((o) => !o.archived || o.id === event.opponent_id);
  const access = useQuery({ queryKey: ['event', event.id, 'accessibility'], queryFn: () => eventEndpoints.accessibility(event.id) });
  const saveAccess = useMutation({
    mutationFn: (input: { notes: string | null; good_to_know_md: string | null; features: { code: string; note: string | null; suppressed?: boolean }[] }) =>
      eventEndpoints.setAccessibility(event.id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['event', event.id, 'accessibility'] }),
    onError: onError,
  });

  const save = useMutation({
    mutationFn: () =>
      eventEndpoints.update(event.id, {
          title: title.trim(),
          subtitle: subtitle.trim() || null,
          visibility,
          featured,
          age_restriction: age.trim() || null,
          minimum_age: minimumAge,
          ticket_limit_per_order: perOrder ? Number(perOrder) : null,
          ticket_limit_per_customer: perCustomer ? Number(perCustomer) : null,
          arrival_note: arrival.trim() || null,
          parking_note: parking.trim() || null,
          post_event_note: postEvent.trim() || null,
          survey_url: survey.trim() || null,
          loyalty_earn: loyaltyEarn,
          loyalty_redeem: loyaltyRedeem,
          opponent_id: opponentId || null,
          voucher_excluded: voucherExcluded,
          playoff_game: playoffGame,
          if_necessary: ifNecessary,
        }),
    onSuccess: refresh,
    onError,
  });

  const status = useMutation({
    mutationFn: ({ next, reason }: { next: string; reason?: string }) => eventEndpoints.setStatus(event.id, next, reason),
    onSuccess: refresh,
    onError,
  });

  const upload = useMutation({
    mutationFn: ({ kind, file }: { kind: 'artwork' | 'hero'; file: File }) => eventEndpoints.uploadImage(event.id, kind, file),
    onSuccess: refresh,
    onError,
  });
  const removeImage = useMutation({
    mutationFn: (kind: 'artwork' | 'hero') => eventEndpoints.removeImage(event.id, kind),
    onSuccess: refresh,
    onError,
  });
  const pick = (kind: 'artwork' | 'hero') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) upload.mutate({ kind, file });
    };
    input.click();
  };

  const readiness = [
    { ok: detail.performances.some((p) => p.status !== 'CANCELLED'), label: `${dateWords().a}` },
    { ok: detail.ticket_types.some((t) => t.active === 1), label: 'a ticket type' },
    { ok: detail.price_zones.length > 0, label: 'a price zone' },
    { ok: (detail.active_price_table?.price_points ?? 0) > 0, label: 'an active price table with prices' },
  ];
  const ready = readiness.every((r) => r.ok);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Lifecycle</p>
          {event.status === 'DRAFT' ? (
            <>
              <ul className="mt-2 space-y-1 text-sm">
                {readiness.map((r) => (
                  <li key={r.label} className={r.ok ? 'text-ok' : 'text-ink-600'}>
                    {r.ok ? '✓' : '○'} {r.label}
                  </li>
                ))}
              </ul>
              <Button variant="primary" className="mt-3" disabled={!ready || !can('events.publish') || status.isPending} onClick={() => status.mutate({ next: 'PUBLISHED' })}>
                Publish event
              </Button>
              {!can('events.publish') ? <p className="mt-1 text-xs text-ink-400">Publishing needs events.publish.</p> : null}
            </>
          ) : (
            <p className="mt-2 text-sm text-ink-600">
              This event is {event.status.toLowerCase()}.
              {event.status === 'PUBLISHED' ? ` Put ${dateWords().many} on sale from the ${dateWords().Many} tab.` : ''}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            {event.status === 'PUBLISHED' && can('events.publish') ? (
              <Button
                variant="danger"
                disabled={status.isPending}
                onClick={() => {
                  const reason = window.prompt(`Why is this event being canceled? Every ${dateWords().one} will be canceled with it.`);
                  if (reason) status.mutate({ next: 'CANCELLED', reason });
                }}
              >
                Cancel event
              </Button>
            ) : null}
            {event.status !== 'ARCHIVED' && can('events.manage') ? (
              <Button
                disabled={status.isPending}
                onClick={() => {
                  if (window.confirm('Archive this event? It leaves every list but keeps its history.')) status.mutate({ next: 'ARCHIVED' });
                }}
              >
                Archive
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4 text-sm text-ink-600">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">At a glance</p>
          <p className="mt-2">
            {detail.performances.length} {dateWords().one}(s) at {detail.venue.name} ({detail.venue.timezone}).
          </p>
          <p>
            {detail.ticket_types.length} ticket type(s), {detail.price_zones.length} price zone(s), {detail.pricing_tiers.length} tier(s).
          </p>
          <p>
            Price table: {detail.active_price_table ? `v${detail.active_price_table.version} active, ${detail.active_price_table.price_points} price(s)` : 'none active'}.
          </p>
          <p>
            Fees: {detail.fee_rules.length} attached. Taxes: {detail.tax_rules.length} attached. Accounting: {event.accounting_mode.replaceAll('_', ' ').toLowerCase()}
            {detail.active_revenue_rule_set ? ` (rule set v${detail.active_revenue_rule_set.version})` : ''}.
          </p>
        </div>
      </div>

      <Panel title="Details" description="What customers see. The slug is the URL; the venue and accounting mode were fixed when the event was created.">
        <form
          className="space-y-4 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="flex flex-wrap items-end gap-4">
            <Field label="Title" htmlFor="ev-title">
              <input id="ev-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!editable} className={`w-80 ${INPUT}`} />
            </Field>
            <Field label="Subtitle" htmlFor="ev-subtitle">
              <input id="ev-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} disabled={!editable} className={`w-80 ${INPUT}`} />
            </Field>
            <Field label="Visibility" htmlFor="ev-visibility">
              <select id="ev-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)} disabled={!editable} className={INPUT}>
                {VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {v.toLowerCase()}
                  </option>
                ))}
              </select>
            </Field>
            <label className="flex items-center gap-2 pb-1.5 text-sm text-ink-900">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} disabled={!editable} />
              featured
            </label>
          </div>
          <div className={features.loyalty ? 'flex flex-wrap items-center gap-6' : 'hidden'}>
            <label className="flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={loyaltyEarn} onChange={(e) => setLoyaltyEarn(e.target.checked)} disabled={!editable} />
              earns loyalty points
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={loyaltyRedeem} onChange={(e) => setLoyaltyRedeem(e.target.checked)} disabled={!editable} />
              rewards may be spent on it
            </label>
            <p className="text-xs text-ink-400">
              Two separate questions: a fundraiser can earn without being discountable. In a cart with other events these apply to this event&rsquo;s share only, and
              the customer is told why in these words: &ldquo;{LOYALTY_RULE}&rdquo;
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            {/* The number is what the calendar filters on; the note is for what a number cannot say (D-322). */}
            <Field label="Ages" htmlFor="ev-min-age">
              <select id="ev-min-age" value={minimumAge} onChange={(e) => setMinimumAge(Number(e.target.value))} disabled={!editable} className={`w-32 ${INPUT}`}>
                {[...new Set<number>([...MINIMUM_AGE_CHOICES, minimumAge])].sort((a, b) => a - b).map((value) => (
                  <option key={value} value={value}>
                    {ageLabel(value)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Age note" htmlFor="ev-age">
              <input id="ev-age" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Valid photo ID required" maxLength={60} disabled={!editable} className={`w-64 ${INPUT}`} />
            </Field>
            <Field label="Max per order" htmlFor="ev-per-order">
              <input id="ev-per-order" value={perOrder} onChange={(e) => setPerOrder(e.target.value.replace(/\D/g, ''))} inputMode="numeric" disabled={!editable} className={`tabular w-24 ${INPUT}`} />
            </Field>
            <Field label="Max per customer" htmlFor="ev-per-customer">
              <input id="ev-per-customer" value={perCustomer} onChange={(e) => setPerCustomer(e.target.value.replace(/\D/g, ''))} inputMode="numeric" disabled={!editable} className={`tabular w-24 ${INPUT}`} />
            </Field>
          </div>
          <fieldset className="rounded-xl border border-line p-4">
            <legend className="px-1 text-sm font-semibold">Game</legend>
            <p className="mb-3 text-xs text-ink-500">
              The team this game is against, and its promotions. The schedule shows both. Copying this event does not carry the team or a promotion added by hand, because each game has its own; a promotion’s rule picks up a copy in its season by itself. Teams are managed on the Opponents page.
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <Field label="Opponent" htmlFor="ev-opponent">
                <select id="ev-opponent" value={opponentId} onChange={(e) => setOpponentId(e.target.value)} disabled={!editable} className={`w-72 ${INPUT}`}>
                  <option value="">None — not a game</option>
                  {opponentChoices.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                      {o.archived ? ' (archived)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <PromotionsPicker eventId={event.id} editable={editable} onError={onError} />
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Vouchers and season passes</p>
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input type="checkbox" checked={voucherExcluded} onChange={(e) => setVoucherExcluded(e.target.checked)} disabled={!editable} />
                Excluded from vouchers
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input type="checkbox" checked={playoffGame} onChange={(e) => setPlayoffGame(e.target.checked)} disabled={!editable} />
                Playoff game
              </label>
              <label className="flex items-center gap-2 pl-6 text-sm text-ink-900">
                <input type="checkbox" checked={ifNecessary} onChange={(e) => setIfNecessary(e.target.checked)} disabled={!editable} />
                If necessary (played only if the series needs it)
              </label>
              <p className="text-xs text-ink-500">
                A comp voucher cannot be used for a game excluded from vouchers (opening day, fireworks nights). Season passes cover this season's games against an opponent, except playoff games. An “if necessary” game shows that badge on the schedule and its page, with a line saying tickets are refunded automatically if it is not played; {dateWords().Many} then offers “Not needed: cancel and refund”. Copying this event does not carry these boxes over.
              </p>
            </div>
          </fieldset>
          <fieldset className="rounded-xl border border-line p-4">
            <legend className="px-1 text-sm font-semibold">What we tell ticket holders</legend>
            <p className="mb-3 text-xs text-ink-500">
              The reminder goes out the day before each {dateWords().one}; the thank-you goes out the morning after. Both are sent automatically to everyone holding a live ticket. Leave a field empty and that line is simply left out.
            </p>
            <div className="space-y-3">
              <Field label="Before they arrive" htmlFor="ev-arrival">
                <textarea id="ev-arrival" value={arrival} onChange={(e) => setArrival(e.target.value)} rows={2} disabled={!editable} placeholder="The entrance is on Front Street. Bags are checked." className={`w-full ${INPUT}`} />
              </Field>
              <Field label="Parking" htmlFor="ev-parking">
                <textarea id="ev-parking" value={parking} onChange={(e) => setParking(e.target.value)} rows={2} disabled={!editable} placeholder="Free in the riverfront lot; overflow on Valley Street." className={`w-full ${INPUT}`} />
              </Field>
              <Field label="After the event" htmlFor="ev-post">
                <textarea id="ev-post" value={postEvent} onChange={(e) => setPostEvent(e.target.value)} rows={2} disabled={!editable} placeholder="Thanks for coming — the photos are on our Facebook page." className={`w-full ${INPUT}`} />
              </Field>
              <Field label="Survey link" htmlFor="ev-survey">
                <input id="ev-survey" value={survey} onChange={(e) => setSurvey(e.target.value)} type="url" disabled={!editable} placeholder="https://…" className={`w-full ${INPUT}`} />
              </Field>
            </div>
          </fieldset>
          {editable ? (
            <Button type="submit" variant="primary" disabled={!title.trim() || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save details'}
            </Button>
          ) : null}
        </form>
      </Panel>

      <Panel title="Images" description={`The card (4:3) is what the events list, home page and ${dateWords().one} pages show; the hero (wide, about 3:1) is the banner across the top of the event page and the card stands in when there is none. PNG, JPEG or WebP, up to 4 MB.`}>
        <div className="grid gap-6 px-5 py-4 md:grid-cols-2">
          {(['artwork', 'hero'] as const).map((kind) => {
            const fileId = kind === 'artwork' ? event.artwork_file_id : event.hero_file_id;
            return (
              <div key={kind}>
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{kind === 'artwork' ? 'Event card' : 'Hero banner'}</p>
                {fileId ? (
                  <img src={`${eventEndpoints.imageUrl(event.id, kind)}?v=${event.updated_at}`} alt="" className={`mt-2 rounded border border-line object-cover ${kind === 'artwork' ? 'aspect-[4/3] w-56' : 'aspect-[3/1] w-full max-w-lg'}`} />
                ) : (
                  <p className="mt-2 text-sm text-ink-400">None yet; the storefront shows a colored gradient with the title.</p>
                )}
                {editable ? (
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => pick(kind)} disabled={upload.isPending}>
                      {upload.isPending ? 'Uploading…' : fileId ? 'Replace image' : 'Upload image'}
                    </Button>
                    {fileId ? (
                      <Button variant="danger" onClick={() => removeImage.mutate(kind)} disabled={removeImage.isPending}>
                        Remove
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Panel>

      <AccessibilityEditor
        title="Accessibility and good to know"
        description="What a customer is told this event offers, and the lines the event page lists. A feature the room already has starts ticked and says so — untick it to say it is not true of this show."
        catalog={access.data?.catalog ?? []}
        selected={access.data?.features ?? []}
        inherited={access.data?.inherited ?? []}
        notes={access.data?.notes ?? null}
        goodToKnow={access.data?.good_to_know_md ?? null}
        editable={editable}
        saving={saveAccess.isPending}
        onSave={(draft) => saveAccess.mutate(draft)}
      />

      {can('events.delete') ? <DeleteEvent eventId={event.id} title={event.title} onError={onError} /> : null}
    </div>
  );
}

/**
 * Deleting a test event (item 314, D-308).
 *
 * The preview is always shown before the button, because the answer is usually
 * "you cannot, somebody bought a ticket" and that is worth finding out by
 * looking rather than by pressing. The title has to be typed: the one way this
 * goes catastrophically wrong is the right button on the wrong event, and an id
 * in a URL is not something anybody reads.
 */
function DeleteEvent({ eventId, title, onError }: { eventId: string; title: string; onError: (error: ApiError) => void }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const preview = useQuery({ queryKey: ['event', eventId, 'deletion'], queryFn: () => eventEndpoints.deletion(eventId), enabled: open });
  const remove = useMutation({
    mutationFn: () => eventEndpoints.remove(eventId, typed),
    // Nothing to refresh: the event this page is about no longer exists.
    onSuccess: () => window.location.assign('/events'),
    onError,
  });

  const refusals = preview.data?.refusals ?? [];
  return (
    <Panel
      title="Delete this event"
      description="Only an event that never sold anything can be deleted. Everything else is archived, which hides it and keeps its history."
      actions={!open ? <Button variant="danger" onClick={() => setOpen(true)}>See what this would take</Button> : null}
    >
      {!open ? null : preview.isPending ? (
        <p className="px-5 py-4 text-sm text-ink-500">Working out what belongs to this event…</p>
      ) : refusals.length > 0 ? (
        <div className="px-5 py-4">
          <ErrorNote>This event cannot be deleted.</ErrorNote>
          <ul className="mt-3 space-y-2 text-sm text-ink-600">
            {refusals.map((refusal) => (
              <li key={refusal.noun}>
                <span className="font-medium text-ink-900">
                  {refusal.noun} ({refusal.rows})
                </span>{' '}
                — {refusal.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="px-5 py-4">
          <p className="text-sm text-ink-600">
            {preview.data?.total_rows ?? 0} rows would be destroyed and cannot be brought back:
          </p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
            {(preview.data?.removals ?? []).map((row) => (
              <li key={row.table} className="tabular">
                {row.table.replaceAll('_', ' ')} ×{row.rows}
              </li>
            ))}
          </ul>
          <label htmlFor="confirm-title" className="mt-4 block text-xs font-semibold tracking-wide text-ink-400 uppercase">
            Type the title to confirm
          </label>
          <input id="confirm-title" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={title} className={`${INPUT} mt-1 w-80`} />
          <div className="mt-3 flex gap-2">
            <Button variant="danger" disabled={typed !== title || remove.isPending} onClick={() => remove.mutate()}>
              {remove.isPending ? 'Deleting…' : 'Delete it permanently'}
            </Button>
            <Button onClick={() => { setOpen(false); setTyped(''); }}>Cancel</Button>
          </div>
        </div>
      )}
    </Panel>
  );
}
